// The rank ladder, derived from the user document and stored nowhere.
//
// There is no rank field and there must not be one. Everything below is
// computed from fields that already exist on every live document, so there is
// no migration, no new Firestore rule, and nothing that can drift out of step
// with the progress bar. (The September 2026 backfill touched
// unansweredQnsIDs and totalQnsInMainCollection, which predate the ladder — it
// added no field for it, and none should ever be added.)
//
// totalCorrect and not totalAttempts. A question leaves the pool only when
// answered correctly, so every correct answer is a distinct question gone for
// good and the count cannot be farmed. Attempts can be: four options, a guess,
// a click. A timeout writes nothing at all, so an attempts ladder would also
// have rewarded clicking and ignored the player who read the question and ran
// out of time.
//
// Below the first rung there is NO name. Callers must render next/toGo there
// and must not invent a label for what the player is not yet — "Yodha, 20 to
// go", never "Novice". Naming the goal pulls; naming the shortfall does not.
//
// Ardharatha is deliberately absent. It is Bhishma's public humiliation of
// Karna in the Udyoga Parva. A player who does not know the term gains nothing
// from it, and a player who does know it is the best player on the site, being
// handed a slur as a reward.
//
// ATIRATHA OUTRANKS MAHARATHA. The order below is correct. Do not "fix" it.
//
// This will look wrong to you, because almost everything written about the
// classification says the reverse. A web search returns Ardharatha -> Rathi ->
// Atirathi -> Maharathi, with Maharathi at the top taking on 720,000, and calls
// Bhishma a maharatha. An LLM asked cold will tell you the same, because that
// is what it read. All of it is popular tradition, and CLAUDE.md already says
// the Critical Edition wins where the two disagree.
//
// Debroy, translating the CE, is unambiguous, in four separate footnotes:
//
//   "An atiratha is an unrivalled warrior, whose valour is unlimited, greater
//    than a maharatha."
//   "A maharatha is a warrior who can take on 10,000 warriors single-handed.
//    An atiratha can take on 60,000."
//   "An atiratha is a great warrior, superior to a maharatha."
//   "Bhishma was an atiratha."
//
// So the popular version has the order backwards, the numbers wrong, and
// Bhishma in the wrong tier. The landing page glosses Atiratha as "unrivalled
// warrior" on the strength of the first quotation.
//
// Reordering RANKS is also not the cheap edit it looks like, which is worth
// knowing before anyone tries it on a hunch. Rank is computed, never stored,
// so a swap silently renames what every live player is called. rankBadge.js
// keys the wheel off the NAME, so the eighteen-spoke wheel that matches the
// certificate would have to be moved to whichever rung ended up last. And the
// GA4 `rank` dimension already holds months of data under these names;
// registration is not retroactive and a parameter cannot be re-registered.
//
// KEEP THIS FILE IMPORT-FREE. tests/rank.test.mjs loads it directly in Node,
// which does not resolve the extensionless imports the rest of the app uses.
//
// No .at(-1) or .findLast() anywhere below. There is no browserslist in
// package.json, so Parcel picks its own default targets, and those two are
// Safari 15.4 — newer than the ?. and ?? this codebase already relies on.
// Indexing costs nothing and removes the question.
//
// `spokes` lives here rather than in rankBadge.js so the ladder has one home.
// It is the count on the badge's wheel, rising to the eighteen the certificate
// carries. See rankBadge.js.
//
// 6, not 4, at the bottom. Four spokes at a legible stroke weight draw a plus
// sign through the disc and the badge reads as a crosshair rather than a wheel
// — checked by rendering it. Six is the fewest that still reads as a wheel.
// The steps are then even, four apart, which is the most spoke density can
// carry at the 24px the header uses.

// THE LAST RUNG STILL HAS NO FIXED NUMBER, and it still must not get one.
//
// initializeUser copies questionsIDsArr into the player's own unansweredQnsIDs
// once, at first sign-in, and never re-reads it. So "the whole quiz" is a size
// each player carries themselves, and ladder() resolves this rung to it.
//
// That used to mean the pools drifted apart permanently: on 11 September 2026,
// 592 of 595 live documents were frozen at 973 while the master list held 1000,
// so a rung hardcoded at 1000 would have been unreachable for every one of them
// and the profile line would have read "27 more to Atiratha" forever.
//
// THAT HAS BEEN REPAIRED, and the repair is why the number stays out of here
// rather than a reason to put it in:
//
//   - backfill-user-pools.mjs ran against production on 11 September 2026 and
//     brought 565 players up to the full 1000. 567 documents now read 1000.
//   - upload-new-questions.mjs deals every newly added question into every
//     unfinished pool as phase two of the upload, so the gap cannot reopen.
//
// So today almost everyone is on the same number — but "almost" is the whole
// point. 28 documents are deliberately NOT: 23 certificate holders and 4
// players who finished without claiming stay at 973 forever, because topping
// them up would take away a certificate they have earned, and one malformed
// document has no pool at all. Hardcode 1000 here and ladder() puts the last
// rung above the pool those 27 can actually finish, so rank() would never call
// them Atiratha again.
//
// It is also the same reason the certificate and the end-of-quiz panel say
// "every question" rather than a number. The landing page is the one place that
// prints 1000, and the comment on that <li> in index.html carries the argument
// for it and its cost.
export const RANKS = [
  { name: "Yodha", at: 25, spokes: 6 },
  { name: "Ratha", at: 100, spokes: 10 },
  { name: "Maharatha", at: 300, spokes: 14 },
  { name: "Atiratha", at: null, spokes: 18 } // resolved per player by ladder()
];

// Only for a document with no totalQnsInMainCollection at all. getUserProfilePanel
// re-reads the live list in that case; here it is a last resort, not a promise.
const FALLBACK_WHOLE = 1000;

// The ladder as it applies to one player: fixed rungs below their pool size,
// then the last rung at the pool size itself.
//
// Rungs at or above the pool are dropped rather than clamped, so the ladder
// stays strictly ascending. That matters locally, where `npm run seed` writes
// ten questions: the ladder is then Atiratha alone, at ten, instead of
// 25/100/300/10 in that order.
export function ladder(totalQuestions) {
  const n = Number(totalQuestions);
  const whole = Number.isFinite(n) && n > 0 ? Math.floor(n) : FALLBACK_WHOLE;

  const rungs = RANKS.filter(r => r.at !== null && r.at < whole);
  rungs.push({ ...RANKS[RANKS.length - 1], at: whole });
  return rungs;
}

// "a Yodha", but "an Atiratha". Three of the four rungs take "a", so writing
// the article into the copy got it wrong exactly once — at the last rung, on
// the most-earned screen in the app.
export function article(name) {
  return /^[aeiou]/i.test(name) ? "an" : "a";
}

// Takes the users/{email} document, not a bare number, because the last rung
// cannot be decided from the count alone. See `finished` below.
export default function rank(userDocument) {
  const doc = userDocument ?? {};
  const rungs = ladder(doc.totalQnsInMainCollection);
  const last = rungs[rungs.length - 1];

  // An EMPTY POOL is the only trustworthy completion signal, and it outranks
  // the count. Verified in production: finishers sit at 970, 971, 972 and 973
  // correct against a pool of 973, all with unansweredQnsIDs empty and a
  // certificate issued. firebaseSetDocWrapper neither awaits nor catches
  // (known issue 3), so an increment can be dropped while the arrayRemove in
  // the same write lands — the count drifts below the truth and never
  // recovers.
  //
  // Going by the count would have told eleven of the twenty-two people who
  // have finished this quiz that they are a Maharatha with two more to go.
  //
  // It is also the same test showLoggedInView and loadQuizRound already use to
  // decide the quiz is over, so the panels agree by construction.
  const n = Number(doc.totalCorrect) || 0;
  const finished =
    Array.isArray(doc.unansweredQnsIDs) && doc.unansweredQnsIDs.length === 0;

  // n > 0 guards a document caught mid-initialisation, where the pool has not
  // been written yet. Nobody finishes with nothing correct.
  if (finished && n > 0) {
    return { name: last.name, attained: last, next: null, toGo: 0 };
  }

  let attained = null;
  let next = null;
  for (const r of rungs) {
    if (n >= r.at) attained = r;
    else if (!next) next = r;
  }

  return {
    name: attained ? attained.name : null,
    attained,
    next,
    toGo: next ? next.at - n : 0
  };
}
